import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DollarSign, TrendingUp, Truck, UserRound, AlertCircle, Check } from 'lucide-react';
import { API, TYPE_LABEL, TypeIcon, fmtDate } from './fleetShared';

export const money = (n, digits = 0) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
const MONTH = (ym) => new Date(`${ym}-01T00:00:00`).toLocaleDateString('en-US', { month: 'short' });

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="bg-white rounded-xl shadow p-4 flex items-start gap-3" data-testid={testid}>
      <div className="p-2 rounded-lg bg-slate-100 text-slate-700"><Icon className="w-5 h-5" /></div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">{label}</div>
        <div className="text-xl font-bold text-slate-900 truncate">{value}</div>
        {sub && <div className="text-xs text-slate-500 truncate">{sub}</div>}
      </div>
    </div>
  );
}

// 12 little bars — one per month — scaled to the biggest month in the table so units compare at a glance
function MonthBars({ months, labels, max, testid }) {
  return (
    <div className="flex items-end gap-[2px] h-7" data-testid={testid}>
      {months.map((v, i) => (
        <div key={labels[i]} title={`${MONTH(labels[i])}: ${money(v, 2)}`} className="w-[9px] rounded-sm bg-blue-500/80 hover:bg-blue-600"
          style={{ height: `${max ? Math.max(v > 0 ? 2 : 0, Math.round((v / max) * 28)) : 0}px` }} />
      ))}
    </div>
  );
}

function MissingCostRow({ m, onSaved }) {
  const [cost, setCost] = useState('');
  const [inv, setInv] = useState(m.invoice_no || '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (cost === '') return;
    setBusy(true);
    try { await axios.put(`${API}/service/${m.id}`, { cost, invoice_no: inv }); toast.success(`${m.equipment_name}: ${money(cost, 2)} saved`); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };
  return (
    <tr className="border-t border-slate-100" data-testid={`fleet-missing-row-${m.id}`}>
      <td className="px-2 py-1.5 whitespace-nowrap text-slate-800">{fmtDate(m.service_date)}</td>
      <td className="px-2 py-1.5 font-medium text-slate-800">{m.equipment_name}</td>
      <td className="px-2 py-1.5 text-slate-700">{m.label}{m.vendor ? <span className="text-slate-500"> · {m.vendor}</span> : ''}</td>
      <td className="px-2 py-1.5"><input value={inv} onChange={e => setInv(e.target.value)} placeholder="Invoice #" data-testid={`fleet-missing-inv-${m.id}`} className="w-28 px-2 py-1 border border-slate-300 rounded text-xs text-slate-900" /></td>
      <td className="px-2 py-1.5">
        <div className="flex items-center gap-1">
          <input type="number" value={cost} onChange={e => setCost(e.target.value)} onKeyDown={e => e.key === 'Enter' && save()} placeholder="$" data-testid={`fleet-missing-cost-${m.id}`} className="w-24 px-2 py-1 border border-slate-300 rounded text-xs text-slate-900" />
          <button onClick={save} disabled={busy || cost === ''} data-testid={`fleet-missing-save-${m.id}`} className="p-1 rounded bg-emerald-600 text-white disabled:opacity-40"><Check className="w-3.5 h-3.5" /></button>
        </div>
      </td>
    </tr>
  );
}

export default function FleetCostsView({ onOpenUnit }) {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState(null);
  const [sort, setSort] = useState('total');
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/costs`, { params: { year } }); setData(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not load costs'); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-8 text-center text-blue-800">Loading costs…</div>;
  const years = Array.from(new Set([...(data.years || []), String(new Date().getFullYear())])).sort().reverse();
  const maxMonth = Math.max(0, ...data.units.flatMap(u => u.months));
  const units = [...data.units].sort((a, b) => sort === 'perkm' ? ((b.cost_per_1000km ?? -1) - (a.cost_per_1000km ?? -1)) : sort === 'name' ? (a.equipment_name || '').localeCompare(b.equipment_name || '') : b.total - a.total);
  const top = data.units[0];
  const topDriver = data.drivers.find(d => !d.operator.startsWith('—'));
  const label = year === 'last12' ? 'last 12 months' : year;

  return (
    <div className="space-y-4" data-testid="fleet-costs-view">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-blue-800">Repairs & maintenance spend for</span>
        <select value={year} onChange={e => setYear(e.target.value)} data-testid="fleet-costs-year" className="px-2 py-1.5 rounded-lg text-sm bg-white text-slate-900 font-semibold outline-none">
          <option value="last12">Last 12 months</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        {data.missing_count > 0 && <span className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1" data-testid="fleet-costs-missing-badge"><AlertCircle className="w-3 h-3" /> {data.missing_count} entr{data.missing_count === 1 ? 'y' : 'ies'} without a cost yet</span>}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={DollarSign} label={`Total · ${label}`} value={money(data.total)} sub={`${data.units.reduce((s, u) => s + u.costed, 0)} costed entries`} testid="fleet-kpi-total" />
        <Kpi icon={TrendingUp} label="Per month" value={money(data.monthly_avg)} sub={`${money(data.monthly_avg * 12)} a year at this rate`} testid="fleet-kpi-monthly" />
        <Kpi icon={Truck} label="Costliest unit" value={top ? top.equipment_name : '—'} sub={top ? `${money(top.total)}${top.operator ? ` · ${top.operator}` : ''}` : 'no costs yet'} testid="fleet-kpi-top-unit" />
        <Kpi icon={UserRound} label="Costliest driver" value={topDriver ? topDriver.operator : '—'} sub={topDriver ? `${money(topDriver.total)} across ${topDriver.units.length} unit${topDriver.units.length === 1 ? '' : 's'}` : 'set the operator on each unit'} testid="fleet-kpi-top-driver" />
      </div>

      <div className="bg-white rounded-xl shadow p-4" data-testid="fleet-costs-by-month">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Fleet spend by month</div>
        <div className="flex items-end gap-1.5 h-24">
          {data.by_month.map((v, i) => {
            const mx = Math.max(...data.by_month, 1);
            return (
              <div key={data.range.months[i]} className="flex-1 flex flex-col items-center justify-end gap-1 h-full" title={money(v, 2)}>
                <div className="text-[10px] text-slate-600">{v ? money(v) : ''}</div>
                <div className="w-full rounded-t bg-blue-600/80" style={{ height: `${Math.max(v > 0 ? 3 : 0, Math.round((v / mx) * 70))}px` }} />
                <div className="text-[10px] text-slate-500">{MONTH(data.range.months[i])}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 bg-white rounded-xl shadow overflow-hidden" data-testid="fleet-costs-units">
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">By unit</div>
            <div className="flex gap-1 text-[11px]">
              {[['total', 'Most spent'], ['perkm', '$ / 1,000 km'], ['name', 'Name']].map(([k, l]) => (
                <button key={k} onClick={() => setSort(k)} data-testid={`fleet-costs-sort-${k}`} className={`px-2 py-0.5 rounded-full ${sort === k ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{l}</button>
              ))}
            </div>
          </div>
          {units.length === 0 ? <p className="p-4 text-sm text-slate-500" data-testid="fleet-costs-empty">No service entries in this period.</p> : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600"><tr>{['Unit', 'Driver', 'By month', 'Entries', '$ / 1,000 km', 'Total'].map(h => <th key={h} className={`px-3 py-2 font-semibold ${['Entries', '$ / 1,000 km', 'Total'].includes(h) ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr></thead>
              <tbody>
                {units.map(u => (
                  <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => onOpenUnit(u.id)} data-testid={`fleet-costs-unit-${u.id}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2"><TypeIcon type={u.equipment_type} className="w-4 h-4 text-slate-500" /><span className="font-semibold text-slate-900">{u.equipment_name}</span>{u.retired && <span className="text-[9px] text-slate-500 border border-slate-300 rounded px-1">retired</span>}</div>
                      <div className="text-[10px] text-slate-500">{TYPE_LABEL[(u.equipment_type || '').toLowerCase()] || u.equipment_type}{u.kinds[0] ? ` · mostly ${u.kinds[0].kind.toLowerCase()}` : ''}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-700">{u.operator || <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2"><MonthBars months={u.months} labels={data.range.months} max={maxMonth} testid={`fleet-costs-bars-${u.id}`} /></td>
                    <td className="px-3 py-2 text-right text-slate-700">{u.costed}{u.missing ? <span className="text-amber-700" title={`${u.missing} without a cost`}> +{u.missing}?</span> : ''}</td>
                    <td className="px-3 py-2 text-right text-slate-700">{u.cost_per_1000km != null ? money(u.cost_per_1000km) : <span className="text-slate-400" title={u.km_span ? `only ${u.km_span.toLocaleString()} km between readings` : 'needs odometer readings on the entries'}>—</span>}</td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900" data-testid={`fleet-costs-total-${u.id}`}>{money(u.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="space-y-3">
          <div className="bg-white rounded-xl shadow p-4" data-testid="fleet-costs-drivers">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">By driver</div>
            {data.drivers.length === 0 ? <p className="text-sm text-slate-500">—</p> : data.drivers.map(d => (
              <div key={d.operator} className="flex items-center justify-between gap-2 py-1 border-t border-slate-100 first:border-0 text-sm" data-testid={`fleet-costs-driver-${d.operator}`}>
                <div className="min-w-0"><div className="font-medium text-slate-800 truncate">{d.operator}</div><div className="text-[10px] text-slate-500 truncate">{d.units.join(', ')}</div></div>
                <div className="font-bold text-slate-900 whitespace-nowrap">{money(d.total)}</div>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-xl shadow p-4" data-testid="fleet-costs-kinds">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">By kind of work</div>
            {data.kinds.map(k => {
              const pct = data.total ? Math.round((k.total / data.total) * 100) : 0;
              return (
                <div key={k.kind} className="py-1 text-xs">
                  <div className="flex justify-between gap-2"><span className="text-slate-700">{k.kind}</span><span className="font-semibold text-slate-900">{money(k.total)} <span className="text-slate-400 font-normal">{pct}%</span></span></div>
                  <div className="h-1.5 bg-slate-100 rounded mt-0.5"><div className="h-1.5 rounded bg-blue-500" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {data.missing_count > 0 && data.can_edit && (
        <div className="bg-white rounded-xl shadow overflow-hidden" data-testid="fleet-costs-missing">
          <div className="px-4 py-2 border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-amber-800 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> Entries without a cost — type the invoice amount as you file it</div>
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600"><tr>{['Date', 'Unit', 'Work', 'Invoice #', 'Cost'].map(h => <th key={h} className="px-2 py-2 text-left font-semibold">{h}</th>)}</tr></thead>
            <tbody>{data.missing.map(m => <MissingCostRow key={m.id} m={m} onSaved={load} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
